import request from '@/utils/request'
import {
  AdjustmentAmountReq,
  AdjustmentAmountRes,
  AdjustmentTypesRes,
  DailySettlementStatisticsReq,
  DailySettlementStatisticsRes,
  PartnerSettlementStatisticsReq,
  PartnerSettlementStatisticsRes,
  SettlementAmountSummaryReq,
  SettlementAmountSummaryRes,
  SettlementConfirmReq,
  SettlementConfirmRes,
  SettlementStatisticsDetailSummaryReq,
  SettlementStatisticsDetailSummaryRes,
  SettlementStatisticsRes,
} from '@/services/settlement/settlementManagement/types'

export const getSettlementStatistics = async (params: ApiPaginationReq<SettlementAmountSummaryReq>) =>
  await request.get<ApiPaginationV2<SettlementStatisticsRes>, ApiPaginationReq<SettlementAmountSummaryReq>>(
    `/api2/mass/v1/settlements/partners/summary`,
    params,
  )

export const getSettlementAmountSummary = async ({ settlementMonth, country }: SettlementAmountSummaryReq) =>
  await request.get<ApiResponseV2<SettlementAmountSummaryRes>, SettlementAmountSummaryReq>(`/api2/mass/v1/settlements/summary`, {
    settlementMonth,
    country, // undefined(전체)면 qsStringify 가 자동 제거. BE 가 A–E 만 국가필터·F(기타조정) 불변
  })

export const getDailySettlementStatistics = async ({ partnerId, settlementMonth, country }: DailySettlementStatisticsReq) =>
  await request.get<
    ApiResponseV2<{ etcAdjustmentAmount: number; items: DailySettlementStatisticsRes[] }>,
    Omit<DailySettlementStatisticsReq, 'partnerId'>
  >(`/api2/mass/v1/settlements/partners/${partnerId}/daily/summary`, { settlementMonth, country })

export const getPartnerSettlementStatistics = async ({ partnerId, ...params }: PartnerSettlementStatisticsReq) =>
  await request.get<ApiPaginationV2<PartnerSettlementStatisticsRes>, Omit<PartnerSettlementStatisticsReq, 'partnerId'>>(
    `/api2/mass/v1/settlements/partners/${partnerId}/daily/details`,
    params,
  )

export const getPartnerSettlementAmountSummary = async ({ partnerId, settlementMonth, country }: DailySettlementStatisticsReq) =>
  await request.get<ApiResponseV2<SettlementStatisticsRes>, Omit<DailySettlementStatisticsReq, 'partnerId'>>(
    `/api2/mass/v1/settlements/partners/${partnerId}/summary`,
    {
      settlementMonth,
      country, // 상단 총액(VAT) — BE 가 A–E 만 국가필터·F 불변
    },
  )

export const postSettlementConfirm = async (params: SettlementConfirmReq) =>
  await request.post<ApiResponseV2<SettlementConfirmRes>, SettlementConfirmReq>(`/api2/mass/v1/settlements/close`, params)

export const getAdjustmentTypes = async () =>
  await request.get<ApiResponseV2<{ items: AdjustmentTypesRes[] }>>(`/api2/mass/v1/adjustment-reason-types`)

export const addAdjustmentAmount = async (params: AdjustmentAmountReq) =>
  await request.post<ApiResponseV2<AdjustmentAmountRes>, AdjustmentAmountReq>(`/api2/mass/v1/etc-adjustment`, params)

export const getSettlementStatisticsDetailSummary = async (params: SettlementStatisticsDetailSummaryReq) =>
  await request.get<ApiResponseV2<SettlementStatisticsDetailSummaryRes>, Omit<SettlementStatisticsDetailSummaryReq, 'partnerId'>>(
    `/api2/mass/v1/settlements/partners/${params.partnerId}/daily/details/summary`,
    {
      settlementMonth: params.settlementMonth,
      serviceType: params.serviceType,
      country: params.country,
    },
  )

export const EXCEL_UPLOAD_ERROR_CODES = {
  EXCEL_ROW_COUNT_LIMIT_EXCEEDED: 'EXCEL_ROW_COUNT_LIMIT_EXCEEDED', // 엑셀  행 개수 제한 초과 (최대 1000건)
  EXCEL_PARTIAL_VALIDATION_FAILURE: 'EXCEL_PARTIAL_VALIDATION_FAILURE', // 일부 행 검증 실패 (message에 실패한 행 번호가 콤마로 구분되어 반환)
  EXCEL_TOTAL_VALIDATION_FAILURE: 'EXCEL_TOTAL_VALIDATION_FAILURE', // 전체 행 검증 실패
  INVALID_EXCEL_FILE: 'INVALID_EXCEL_FILE', // 엑셀 파일 형식 오류
} as const

export type ExcelUploadErrorCode = (typeof EXCEL_UPLOAD_ERROR_CODES)[keyof typeof EXCEL_UPLOAD_ERROR_CODES]

export const validateExcelEtcAdjustment = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  return await request.post<ApiResponseV2<boolean>, FormData>(`/api2/mass/v1/etc-adjustment/excel-validation`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const uploadExcelEtcAdjustment = async (file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  // mass ExcelUploadResult DTO = savedCount 단일 필드 (all-or-nothing). 현재 소비부는 카운트 미표시라 무증상이나 계약 일치를 위해 정정.
  return await request.post<ApiResponseV2<{ savedCount: number }>, FormData>(
    `/api2/mass/v1/etc-adjustment/excel-upload`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
    },
  )
}
